import { relations } from 'drizzle-orm';
import { jsonb, pgEnum, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
import { organisation } from './organisation.js';
export const LicenceType = z.enum(['limited', 'lifetime']);
export const licenceTypeEnum = pgEnum('licence_type', [LicenceType.enum.limited, LicenceType.enum.lifetime]);
/**
 * Soft limits baked into the signed licence.
 *
 * `concurrentUsers` caps how many sessions may be active at once on the
 * on-premise installation. It is enforced by the HMS server against active
 * sessions; a null or absent value means the installation is not capped.
 */
export const LicenceLimits = z.object({
    concurrentUsers: z.int().min(1).nullable().optional(),
});
export const licence = pgTable('licence', {
    id: uuidv7('id').primaryKey(),
    organisationId: uuid('organisation_id')
        .notNull()
        .references(() => organisation.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    /**
     * TOFU (Trust On First Use) anchor — generated by the CAP at mint time and embedded in the JWT.
     *
     * On first use the hospital's server writes this to an immutable file.
     *
     * Renewals are only accepted if the incoming JWT's installationId matches the stored value, preventing licence reuse across systems.
     *
     * See `docs/licenses.md` for more info.
     */
    installationId: uuid('installation_id').notNull(),
    /**
     * The signed JWT (RS256) — the canonical licence record.
     *
     * Everything else in this row is derived from it for queryability.
     *
     * Stored so admins can re-download licences without re-minting (avoiding a new installationId that would break the hospital's TOFU anchor).
     *
     * The JWT should be saved on the hospital's server, which is responsible of decoding and verifying it, using the `LICENCE_PUBLIC_KEY` env variable.
     */
    jwt: text('jwt').notNull(),
    type: licenceTypeEnum('type').notNull(),
    limits: jsonb('limits').$type().notNull().default({}),
    country: text('country').notNull(),
    issuedAt: timestamp('issued_at').notNull(),
    expiresAt: timestamp('expires_at'),
    revokedAt: timestamp('revoked_at'),
    ...timestamps(),
    ...userActions(),
});
export const licenceRelations = relations(licence, ({ one }) => ({
    organisation: one(organisation, { fields: [licence.organisationId], references: [organisation.id] }),
}));
//# sourceMappingURL=licence.js.map